export type AccountConnectionState =
  | "clerk-loading"
  | "signed-out"
  | "acquiring-session"
  | "connecting-account"
  | "workspace-ready"
  | "authentication-error"
  | "bootstrap-error";

type TokenGetter = () => Promise<string | null>;
type BootstrapFetch = (token: string, signal: AbortSignal) => Promise<Response>;
type StateListener = (state: AccountConnectionState) => void;

interface AttemptInput {
  fetchBootstrap: BootstrapFetch;
  getToken: TokenGetter;
  sessionId: string;
}

export interface AccountBootstrapControllerOptions {
  bootstrapTimeoutMs?: number;
  tokenTimeoutMs?: number;
}

class AttemptTimeoutError extends Error {}

function withTimeout<T>(work: Promise<T>, timeoutMs: number, onTimeout?: () => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeout = setTimeout(() => {
      onTimeout?.();
      reject(new AttemptTimeoutError("Authentication handoff timed out"));
    }, timeoutMs);
    void work.then(
      (value) => {
        clearTimeout(timeout);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

export class AccountBootstrapController {
  private readonly bootstrapTimeoutMs: number;
  private readonly tokenTimeoutMs: number;
  private generation = 0;
  private input: AttemptInput | null = null;
  private listener: StateListener;
  private sessionId: string | null = null;
  private abortController: AbortController | null = null;
  state: AccountConnectionState = "clerk-loading";
  accountId: string | null = null;

  constructor(listener: StateListener, options: AccountBootstrapControllerOptions = {}) {
    this.listener = listener;
    this.bootstrapTimeoutMs = options.bootstrapTimeoutMs ?? 15_000;
    this.tokenTimeoutMs = options.tokenTimeoutMs ?? 10_000;
  }

  setListener(listener: StateListener) {
    this.listener = listener;
  }

  sync(
    session: { state: "config-missing" | "loading" | "signed-out" | "signed-in"; sessionId: string | null },
    getToken: TokenGetter,
    fetchBootstrap: BootstrapFetch,
  ) {
    if (session.state === "config-missing" || session.state === "loading") {
      this.cancel();
      this.sessionId = null;
      this.input = null;
      this.transition("clerk-loading");
      return;
    }
    if (session.state === "signed-out") {
      this.cancel();
      this.sessionId = null;
      this.input = null;
      this.transition("signed-out");
      return;
    }
    if (!session.sessionId) {
      this.cancel();
      this.input = null;
      this.transition("authentication-error");
      return;
    }

    this.input = { fetchBootstrap, getToken, sessionId: session.sessionId };
    if (this.sessionId === session.sessionId) return;
    this.sessionId = session.sessionId;
    this.start();
  }

  retry() {
    if (this.input) this.start();
  }

  dispose() {
    this.cancel();
    this.sessionId = null;
    this.input = null;
  }

  private cancel() {
    this.accountId = null;
    this.generation += 1;
    this.abortController?.abort();
    this.abortController = null;
  }

  private transition(state: AccountConnectionState) {
    this.state = state;
    this.listener(state);
  }

  private start() {
    const input = this.input;
    if (!input) return;
    this.cancel();
    const generation = this.generation;
    this.transition("acquiring-session");
    console.info("[auth] acquiring-session");

    void withTimeout(input.getToken(), this.tokenTimeoutMs)
      .then(async (token) => {
        if (generation !== this.generation) return;
        if (!token) throw new Error("Session token unavailable");
        this.transition("connecting-account");
        console.info("[auth] connecting-account");
        const abortController = new AbortController();
        this.abortController = abortController;
        const response = await withTimeout(
          input.fetchBootstrap(token, abortController.signal),
          this.bootstrapTimeoutMs,
          () => abortController.abort(),
        );
        if (generation !== this.generation) return;
        if (response.status === 401) throw new Error("Authentication rejected");
        if (!response.ok) throw new AttemptTimeoutError("Account bootstrap unavailable");
        const payload: unknown = await withTimeout(response.json(), this.bootstrapTimeoutMs, () => abortController.abort());
        if (generation !== this.generation) return;
        if (!payload || typeof payload !== "object" || typeof (payload as { accountId?: unknown }).accountId !== "string") {
          throw new AttemptTimeoutError("Account bootstrap response invalid");
        }
        this.abortController = null;
        this.accountId = (payload as {accountId:string}).accountId;
        this.transition("workspace-ready");
        console.info("[auth] workspace-ready");
      })
      .catch((error: unknown) => {
        if (generation !== this.generation) return;
        this.abortController = null;
        const authenticationError =
          this.state === "acquiring-session" || (error instanceof Error && error.message === "Authentication rejected");
        this.transition(authenticationError ? "authentication-error" : "bootstrap-error");
        console.info(authenticationError ? "[auth] authentication-error" : "[auth] bootstrap-error");
      });
  }
}
